"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslation } from "@/app/i18n/client";
import { AddressWithLocation, defaultAddressWithLocation } from "@/types/location";
import SearchField, { SearchProps } from "@/utils/SearchField";
import { useGoogleMaps } from "@/utils/google-maps-context";
import { autocompleteAddress, AutocompletePrediction } from "@/utils/placesApiCalls";
import AddressBlock from "./AddressBlock";
import { useEffect, useState } from "react";
type PlaceAutocompleteProps = {
  /**
   * setLocation is used to pass the chosen location back to the parent component.
   * @param address An Address object with a formatted string, and all of the address fields in individual values. Furthermore, it contains the coordinates of the location.
   * @returns
   */
  setLocation: (address: AddressWithLocation) => void;
  className?: string; // A Classname that is applied to the wrapper component. Used to add styling where needed.
  resultsListClassName?: string; // A Classname for styling the results list. Is applied to the div wrapping the ul and all li with the buttons.
  label?: string; // If present, a label is added above the input field.
  name: string; // An identifier for the input field.
  placeholder: string; // A Placeholder value for the input field
  useAddressFields?: boolean; // This determines if the Address Fields Block is used instead of the single Search Field
  initialValue?: AddressWithLocation; // This sets the initial value of the input field. This will not be searched by google maps.
  type?: "locality" | "street_address"; //The type here can be anything from this list: https://developers.google.com/maps/documentation/javascript/supported_types . It is used to get targeted results.
  onClear?: () => void;
  disabled?: boolean;
  "data-sentry-unmask"?: boolean;
};

/**
 * Place Autocomplete is a tool for Searching Google Maps Locations and having a fully filled address object returned.
 * In its default configuration, it features an input field, which can be used to search for Google Maps locations. Results are shown in a list below the text field and can be selected by clicking on them or by using the Arrow Keys and Enter.
 * When a Result is selected, its address is displayed in the input field and the address object is passed to setLocation().
 * The PlaceAutocomplete can search for cities, addresses or any result. More types can be found by following the link above and be added.
 * @param props Look above for an explanation of the props
 * @returns
 */
export default function PlaceAutocomplete(props) {
  const $ = _c(17);
  const {
    i18n
  } = useTranslation();
  const {
    isLoaded: isMapsLibLoaded,
    loadError: mapsLibLoadError,
    loadNow
  } = useGoogleMaps();
  const [address, setAddress] = useState(props.initialValue ?? defaultAddressWithLocation);
  let t0;
  let t1;
  if ($[0] !== props.initialValue) {
    t0 = () => {
      if (props.initialValue) {
        setAddress(props.initialValue);
      }
    };
    t1 = [props.initialValue];
    $[0] = props.initialValue;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== props.label || $[4] !== props.name) {
    t2 = props.label && <label htmlFor={props.name}>{props.label}</label>;
    $[3] = props.label;
    $[4] = props.name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== address || $[7] !== i18n || $[8] !== isMapsLibLoaded || $[9] !== loadNow || $[10] !== mapsLibLoadError || $[11] !== props) {
    t3 = props.useAddressFields ? <AddressBlock id={props.name} address={address} onLocationChanged={address_0 => {
      setAddress(address_0);
      props.setLocation(address_0);
    }} disabled={props.disabled} /> : <SearchField inputId={props.name} initialValue={props.initialValue?.address} inputOnSelect="value" maxQueryResults={4} minCharacters={3} searchResultsClassName={props.resultsListClassName} onClear={() => {
      setAddress(defaultAddressWithLocation);
      props.onClear?.();
    }} httpFetchMethod={(searchTerm, abortSignal) => new Promise((resolve, reject) => {
      if (abortSignal?.aborted) {
        reject(new Error("Aborted"));
      }
      abortSignal?.addEventListener("abort", () => {
        reject(new Error("Aborted"));
      });
      if (!isMapsLibLoaded) {
        loadNow();
        setTimeout(() => {
          autocompleteAddress(searchTerm, i18n.language, props.type ? [props.type] : undefined).then(res => {
            const actualRes = res.map(_temp);
            resolve(actualRes);
          });
        }, 3000);
      } else {
        if (mapsLibLoadError) {
          return mapsLibLoadError;
        } else {
          autocompleteAddress(searchTerm, i18n.language, props.type ? [props.type] : undefined).then(res_0 => {
            resolve(res_0.map(_temp2));
          });
        }
      }
    })} resultGenerator={_temp3} delayQuery={350} onSelect={async addrPred => {
      const addrWL_1 = await addrPred.fillPlace();
      setAddress(addrWL_1);
      props.setLocation?.(addrWL_1);
    }} />;
    $[6] = address;
    $[7] = i18n;
    $[8] = isMapsLibLoaded;
    $[9] = loadNow;
    $[10] = mapsLibLoadError;
    $[11] = props;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== props.className || $[14] !== t2 || $[15] !== t3) {
    t4 = <div className={props.className}>{t2}{t3}</div>;
    $[13] = props.className;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
function _temp3(res_1) {
  return <div>{res_1.formattedAddress}</div>;
}
function _temp2(addrWL_0, i_0) {
  const WithSearchTerm_0 = {
    ...addrWL_0,
    id: i_0,
    score: i_0,
    value: addrWL_0.formattedAddress
  };
  return WithSearchTerm_0;
}
function _temp(addrWL, i) {
  const WithSearchTerm = {
    ...addrWL,
    id: i,
    score: i,
    value: addrWL.formattedAddress
  };
  return WithSearchTerm;
}